// Relative path: src/components/Recommendations/AiCommentary.js

import React, { useState, useEffect, useRef } from "react";
import recUtils from "../../utils/RecUtils";
import AiCommentaryRenderer from "../AiCommentaryRenderer";

/**
 * Wraps AiCommentaryRenderer by fetching and enriching:
 *   • ai_commentary  from the matching result in results_table.data
 *   • formData       from recommendationDoc.user_input
 *   • reviewerMetadata with display-friendly site names & logos
 *
 * Props:
 *  - recommendation_id: string
 *  - clinic_only_name: string
 */
export default function AiCommentary({ recommendation_id, clinic_only_name }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState(null);
  const [aiCommentary, setAiCommentary] = useState(null);
  const [reviewerMetadata, setReviewerMetadata] = useState({});
  const [satisfactionData, setSatisfactionData] = useState({
    clinicSatisfaction: 4.5,
    avgSatisfaction: 3.2
  });

  // Create a unique ID for this commentary section based on clinic name
  const commentaryId = `ai-commentary-${clinic_only_name.replace(/\s+/g, '-').toLowerCase()}`;

  // Clear all profile picture assignments when component unmounts
  useEffect(() => {
    return () => {
      // Only needed for the first clinic to initialize the profile pictures
      if (clinic_only_name && aiCommentary) {
        const reviewIds = Object.keys(aiCommentary)
          .filter(k => /^ref_\d+_review_id$/.test(k))
          .map(k => aiCommentary[k])
          .filter(Boolean);
          
        // Release all profile pictures associated with this clinic's reviews
        reviewIds.forEach(id => {
          recUtils.releaseProfilePicture(id);
        });
      }
    };
  }, [clinic_only_name, aiCommentary]);

  useEffect(() => {
    const load = async () => {
      try {
        // 1) Load or reuse the recommendation document
        if (recUtils.getRecommendationId() !== recommendation_id) {
          const ok = await recUtils.fetchRecommendationFromId(recommendation_id);
          if (!ok) throw new Error(recUtils.getError());
        }
        const docData = recUtils.recommendationDoc;
        if (!docData) throw new Error("No recommendation data available");

        // 2) Pull formData
        setFormData(docData.user_input || {});

        // 3) Find this clinic's ai_commentary
        const results = docData.results_table?.data || [];
        const match = results.find(r => r.clinic_only_name === clinic_only_name);
        if (!match || !match.ai_commentary) {
          setError("No AI commentary found for this clinic");
          setLoading(false);
          return;
        }
        setAiCommentary(match.ai_commentary);

        // 4) Extract all referenced review IDs
        const reviewIds = Object.keys(match.ai_commentary)
          .filter(k => /^ref_\d+_review_id$/.test(k))
          .map(k => match.ai_commentary[k])
          .filter(Boolean);

        // 5) Fetch and enrich each review doc - simplified with some batching
        const meta = {};
        
        // Process reviews in small batches to avoid overwhelming low-tier devices
        const batchSize = 2;
        for (let i = 0; i < reviewIds.length; i += batchSize) {
          const batch = reviewIds.slice(i, i + batchSize);
          await Promise.all(batch.map(async id => {
            const raw = await recUtils.fetchReviewFromId(id);
            if (!raw) return;

            // Use centralized method for website source info
            const websiteInfo = recUtils.getWebsiteSourceInfo(raw);

            // Enrich and store
            meta[id] = {
              ...raw,
              external_url: websiteInfo.external_url,
              external_website: websiteInfo.displayName,
              siteLogo: websiteInfo.siteLogo
            };
          }));

          // Small delay between batches to prevent overwhelming
          if (i + batchSize < reviewIds.length) {
            await new Promise(resolve => setTimeout(resolve, 50));
          }
        }

        setReviewerMetadata(meta);

        // 6) Fetch satisfaction comparison data
        try {
          const satData = await recUtils.getSatisfactionComparisonData(clinic_only_name);
          setSatisfactionData(satData);
        } catch (satError) {
          console.error("Error fetching satisfaction data:", satError);
          // Continue with default values if there's an error
        }
      } catch (e) {
        console.error("AiCommentary load error", e);
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [recommendation_id, clinic_only_name]);

  if (loading) {
    return (
      <div className="flex justify-center py-6">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-mm_blue" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-red-500 text-center py-4">
        ⚠️ {error}
      </div>
    );
  }
  return (
    <div id={commentaryId}>
      <AiCommentaryRenderer
        selectedClinic={clinic_only_name}
        aiCommentary={aiCommentary}
        reviewerMetadata={reviewerMetadata}
        formData={formData}
        satisfactionData={satisfactionData}
      />
    </div>
  );
}